import React, { type ReactElement } from 'react';

import ArticleAsset from '@components/FeedItems/ArticleAsset';
import IconLock from '@components/Images/Rebranding/IconLock';
import NameList from '@components/NameList';
import PaywallFullBleed from '@components/Paywall/FullBleed';
import TimeAgo from '@components/TimeAgo';
import { useBlueConicPaywallDialogue } from '@hooks';

import { ThemeViewsProps } from '../interfaces';

interface LockedArticleProps extends ThemeViewsProps {
  offerCode?: string;
  shareGuid?: string;
}

const LockedArticle = ({
  article: {
    authors,
    featuredEntityTopic,
    id,
    isPro,
    picture,
    publishedAtIso,
    title,
    posterPicture,
  },
  env,
  offerCode,
  shareGuid,
}: LockedArticleProps): ReactElement => {
  const { paywallProps, isArticlePaywallPropsLoading } = useBlueConicPaywallDialogue({
    articleId: id,
    isTwoOptPaywall: true,
    offerCode,
    shareGuid,
  });

  return (
    <div className="relative mx-auto w-full max-w-[1154px] p-16 font-normal">
      <p className="text m-0 min-h-[36px] text-center font-suisse-intl-mono text-overline-md font-normal text-grayscale-charcoal sm:text-caption-lg md:text-feed-headline-sm">
        {!isArticlePaywallPropsLoading && (
          <>
            {paywallProps?.title
              ? paywallProps.title
              : featuredEntityTopic && (
                  <>Behind the scenes of the biggest news in {featuredEntityTopic}</>
                )}
          </>
        )}
      </p>
      <div className="mt-16 grid w-full grid-cols-1 gap-0 sm:mt-24 sm:grid-cols-2 sm:gap-x-24 sm:px-24 md:grid-rows-[auto_auto_auto] md:gap-x-32 lg:grid-cols-[auto_auto]">
        <header className="md:col-span-1">
          <h1 className="m-0 font-suisse-works text-headline-xs text-primary-black sm:text-headline-md">
            {title}
          </h1>
          <span className="font-suisse-screen text-disclosure-md text-grayscale-charcoal">
            <NameList authors={authors} authorClassName="author-name-body" />
          </span>
          {publishedAtIso && (
            <TimeAgo
              className="mb-0 mt-4 block font-suisse-intl-mono text-overline-sm font-normal text-grayscale-gray sm:text-overline-md lg:text-overline-sm xl:text-overline-md"
              date={publishedAtIso}
            />
          )}
        </header>
        <figure
          className="relative m-auto mt-8 w-full sm:col-start-2 sm:row-start-1 sm:mt-0 md:row-span-3 lg:w-[634px] lg:pr-24"
          aria-labelledby="caption-text"
        >
          <ArticleAsset
            title={title}
            id={id}
            picture={picture}
            env={env}
            wrapperClassName="aspect-[16/9] w-full leading-[0]"
            className="article-hero-image aspect-[16/9] h-auto w-full"
            posterPicture={posterPicture}
          />
        </figure>

        <div className="relative mt-8 h-16 text-center font-suisse-intl-mono text-overline-sm after:absolute after:left-0 after:top-1/2 after:h-[1px] after:w-full after:-translate-y-1/2 after:bg-grayscale-charcoal after:content-[''] sm:col-span-2 sm:-mx-24 sm:mt-16 sm:text-overline-md md:col-span-1 md:col-start-1 md:row-start-2 md:-mr-32 md:ml-0 lg:mt-24">
          <span className="relative z-10 inline bg-white px-8 text-grayscale-charcoal dark:bg-[var(--white)]">
            <IconLock className="h-12 w-12 pt-[1px]" /> Subscribe to unlock
          </span>
        </div>
        <div className="sm:col-span-2 md:col-span-1 md:row-start-3 xl:min-w-[520px]">
          {!isArticlePaywallPropsLoading && <PaywallFullBleed isPro={isPro} {...paywallProps} />}
        </div>
      </div>
    </div>
  );
};

export default LockedArticle;
